import React, { useState } from 'react';

// Keyboard key badge component
function KeyBadge({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center px-2 py-1 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded shadow-sm min-w-[28px]">
      {children}
    </kbd>
  );
}

// Shortcut row component
function ShortcutRow({ keys, description }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600">{description}</span>
      <div className="flex items-center gap-1">
        {Array.isArray(keys) ? (
          keys.map((key, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-gray-400 text-xs mx-1">or</span>}
              <KeyBadge>{key}</KeyBadge>
            </React.Fragment>
          ))
        ) : (
          <KeyBadge>{keys}</KeyBadge>
        )}
      </div>
    </div>
  );
}

// Section component
function ShortcutSection({ title, shortcuts, icon }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        {icon && <span className="text-lg">{icon}</span>}
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">{title}</h3>
      </div>
      <div className="bg-gray-50 rounded-lg p-3">
        {shortcuts.map((shortcut, index) => (
          <ShortcutRow key={index} keys={shortcut.keys} description={shortcut.description} />
        ))}
      </div>
    </div>
  );
}

function ShortcutsPanel({ isOpen, onClose }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Detect if user is on Mac
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const modKey = isMac ? '⌘' : 'Ctrl';

  // Define all shortcuts organized by category
  const shortcutCategories = [
    {
      title: 'Selection',
      icon: '🎯',
      shortcuts: [
        { keys: 'V', description: 'Select tool (default)' },
        { keys: 'Esc', description: 'Deselect all' },
        { keys: `${modKey}+A`, description: 'Select all shapes' },
      ]
    },
    {
      title: 'Drawing Tools',
      icon: '✏️',
      shortcuts: [
        { keys: 'R', description: 'Rectangle tool' },
        { keys: 'C', description: 'Circle tool (future)' },
        { keys: 'T', description: 'Text tool (future)' },
        { keys: 'L', description: 'Line tool (future)' },
      ]
    },
    {
      title: 'Editing',
      icon: '✂️',
      shortcuts: [
        { keys: ['Delete', 'Backspace'], description: 'Delete selected' },
        { keys: `${modKey}+C`, description: 'Copy selected' },
        { keys: `${modKey}+V`, description: 'Paste' },
        { keys: `${modKey}+D`, description: 'Duplicate selected' },
        { keys: `${modKey}+Z`, description: 'Undo' },
        { keys: `${modKey}+Shift+Z`, description: 'Redo' },
        { keys: `${modKey}+G`, description: 'Group selected shapes' },
        { keys: `${modKey}+Shift+G`, description: 'Ungroup shapes' },
      ]
    },
    {
      title: 'Navigation',
      icon: '🧭',
      shortcuts: [
        { keys: 'Space+Drag', description: 'Pan canvas' },
        { keys: ['+', '='], description: 'Zoom in' },
        { keys: '-', description: 'Zoom out' },
        { keys: '↑ ↓ ← →', description: 'Move selected 1px' },
        { keys: 'Shift+Arrow', description: 'Move selected 10px' },
        { keys: 'Mouse Wheel', description: 'Zoom at cursor' },
      ]
    },
    {
      title: 'Layers',
      icon: '📚',
      shortcuts: [
        { keys: `${modKey}+]`, description: 'Bring to front' },
        { keys: `${modKey}+[`, description: 'Send to back' },
        { keys: `${modKey}+Shift+]`, description: 'Bring forward' },
        { keys: `${modKey}+Shift+[`, description: 'Send backward' },
      ]
    },
    {
      title: 'View',
      icon: '👁️',
      shortcuts: [
        { keys: `${modKey}+'`, description: 'Toggle grid' },
        { keys: `${modKey}+0`, description: 'Reset zoom to 100%' },
        { keys: `${modKey}+1`, description: 'Zoom to fit' },
        { keys: '?', description: 'Show this shortcuts panel' },
      ]
    },
  ];

  // Filter shortcuts based on search term
  const filteredCategories = searchTerm
    ? shortcutCategories.map(category => ({
        ...category,
        shortcuts: category.shortcuts.filter(
          shortcut =>
            shortcut.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (Array.isArray(shortcut.keys)
              ? shortcut.keys.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()))
              : shortcut.keys.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      })).filter(category => category.shortcuts.length > 0)
    : shortcutCategories;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden pointer-events-auto transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <span>⌨️</span>
                  <span>Keyboard Shortcuts</span>
                </h2>
                <p className="text-indigo-100 text-sm mt-1">
                  Master CollabCanvas with these keyboard shortcuts
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                aria-label="Close shortcuts panel"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Search bar */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="relative">
              <svg 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search shortcuts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg mb-2">No shortcuts found</p>
                <p className="text-sm">Try a different search term</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredCategories.map((category, index) => (
                  <ShortcutSection
                    key={index}
                    title={category.title}
                    icon={category.icon}
                    shortcuts={category.shortcuts}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <KeyBadge>?</KeyBadge>
                <span>Press anytime to open this panel</span>
              </div>
              <div className="flex items-center gap-2">
                <KeyBadge>Esc</KeyBadge>
                <span>to close</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ShortcutsPanel;

